import SimpleLinearRegression from 'ml-regression-simple-linear';
import fetchGzipFromR2AsJsonViaCfFunctions from '../../utilities/r2Fetch';

import { climateAttrs, selectClimateAttrById } from '../constants/climateAttrs';

export const getClimateAttrNameById = (climateAttrId) => {
  return selectClimateAttrById(climateAttrId).name;
};

// Handles updating climate data
export const fetchClimateData = (climateAttrId) => {
  const climateAttr = climateAttrs.find((attr) => attr.id === climateAttrId);
  return fetchGzipFromR2AsJsonViaCfFunctions(climateAttr.fileName);
};

const getClimateTrends = (data, start, end, minYear) => {
  //get the trend/decade for each state/county of an attr given the start and the end year
  let startIndex = start - minYear;
  let endIndex = end - minYear;
  let x = [];

  for (let i = start; i <= end; i++) {
    x.push(i);
  }

  try {
    let result = {};
    Object.entries(data).forEach(([attr, attrData]) => {
      if (attr !== 'years') {
        Object.entries(attrData).forEach(([location, locationData]) => {
          let y = locationData.slice(startIndex, endIndex + 1);

          let trend = new SimpleLinearRegression(x, y).slope * 10;
          result[location] = trend;
        });
      }
    });
    return result;
  } catch {
    return {};
  }
};

export const updateClimateTrends = ({ state, county }, { start, end, min }) => {
  const stateTrends = getClimateTrends(state, start, end, min);
  const countyTrends = getClimateTrends(county, start, end, min);

  return { state: stateTrends, county: countyTrends };
};

const getClimateProjection = (location, attr, grid, emission, isStation) => {
  let thisAttr = selectClimateAttrById(attr);
  let month = thisAttr['month'];
  let thisAttrElems = thisAttr['params']['elems'][0];
  
  let loc, a_r;
  if (isStation) {
    loc = { loc: location };
    a_r = {};
  } else {
    loc = {
      state: location.length <= 2 ? location : undefined,
      county: location.length > 2 ? location : undefined
    };
    a_r = { area_reduce: location.length > 2 ? 'county_mean' : 'state_mean' };
  }

  let data = {
    ...loc,
    // grid: `loca:${grid}:${emission}`,          // old grid call
    grid: `loca2:${grid}:${emission}`,
    sdate: 1951 + month,
    edate: 2099 + month,
    elems: [
      {
        name: thisAttrElems['name'],
        interval: thisAttrElems['interval'],
        duration: thisAttrElems['duration'],
        reduce: thisAttrElems['reduce'],
        ...a_r
      },
    ],
  };

  if (month !== '') {
    data['elems'] = [
      {
        elem: {
          name: thisAttrElems['name'],
          interval: [0, 1],
          duration: 1,
          reduce: thisAttrElems['reduce'],
          ...a_r,
        },
        interval: thisAttrElems['interval'],
        duration: thisAttrElems['duration'],
        reduce: thisAttrElems['reduce'] === 'mean' ? 'mean' : 'sum',
        ...a_r,
      },
    ];
  }

  return fetch(`${window.location.protocol}//grid2.rcc-acis.org/GridData`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/JSON' },
    body: JSON.stringify(data),
  })
    .then((res) => {
      return res.json();
    })
    .then(({ data }) => {
      if (isStation) {
        return data.map((year) => [parseInt(year[0].slice(0, 4)), year[1]]);
      } else {
        return data.map((year) => [parseInt(year[0].slice(0, 4)), year[1][location]]);
      }
    }
    );
};

export const fetchClimateProjection = async (location, attr, isStation=false) => {
  const [intermediate, high, veryHigh] = await Promise.all(
    // ['rcp45', 'rcp85'].map(async (emissionScenario) => {         // old emissions names for old grid call
    ['ssp245', 'ssp370', 'ssp585'].map(async (emissionScenario) => {
      let series = await Promise.all(
        ['wmean', 'allmin', 'allmax'].map(async (grid) => {
          let result = await getClimateProjection(
            location,
            attr,
            grid,
            emissionScenario,
            isStation
          );
          return result;
        })
      );

      const currYearStr = new Date().getFullYear();
      const currYearIdx = series[0].findIndex((arr) => arr[0] === currYearStr);

      const range = [];
      for (let i = currYearIdx; i < series[1].length; i++) {
        range.push([...series[1][i], series[2][i][1]]);
      }

      return {
        pastMean: series[0].slice(currYearIdx - 30, currYearIdx),
        mean: series[0].slice(currYearIdx),
        range,
      };
    })
  );

  return {intermediate, high, veryHigh};
};

export const fetchStationData = async (args) => {
  const [pastResponse, projectedResponse] = await Promise.all([
    fetch(
      `${window.location.protocol}//data.rcc-acis.org/StnData`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(args.params),
      }
    ),
    fetchClimateProjection(args.loc, args.attr, true)
  ]);

  let pastData = await pastResponse.json();
  pastData = pastData.data.map(([strYear, strVal]) => [parseInt(strYear), parseInt(strVal)]);
  if (pastData[pastData.length - 1][0] === new Date().getFullYear()) {
    pastData.pop();
  }

  return {
      past: pastData,
      projected: projectedResponse
    };
};